import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../../@types/redux';
import { dataSetSizes } from '../../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../../services/appFunctionsService';
import { ICommodity } from '../../../../@types/model/masterData/commodity/commodity';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../../store/general/Functions';
import SizeHttpService from '../../../../services/http/masterData/sizeHttpService';
import SizeForm from './form/SizeForm';
import PackRelatedDataHttpService from '../../../../services/http/masterData/packRelatedDataHttpService';
import { dataSetAllPackRelatedData } from '../../../../store/masterData/Functions';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../../@types/other';
import { setSizeMasterDataIndexedDB, syncMasterData } from '../../../../services/masterDataSyncService';
import { useLocation } from 'react-router-dom';
import SizeModelHelper from '../../../../@types/model/masterData/size/sizeModelHelper';
import UserHelper from '../../../../services/helper/userHelper';
import { RightGrantsEnum } from '../../../../@types/enum/rightGrantsEnum';
import { ISize } from '../../../../@types/model/masterData/size/size';
import { ISizeFormValues } from '../../../../@types/model/masterData/size/sizeFormValues';

interface ISizeScreenProps {
    selectedCommodity : ICommodity | null;
}

const SizeScreen = (props : ISizeScreenProps) : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const sizes = useAppSelector(x => x.masterData.sizes);
    const packs = useAppSelector(x => x.masterData.packs);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectedSize, setSelectedSize] = useState<ISize | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
    const [deletingSize, setDeletingSize] = useState<ISize | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/
    
    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await PackRelatedDataHttpService.getAllPackRelatedData();
                dataSetAllPackRelatedData(res.data);
            } catch (error : unknown) {
                const apiError = error as IApiError | undefined;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading sizes.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : ISizeFormValues) => {
        setIsLoading(true);

        try {
            const upsert = SizeModelHelper.createUpsert(values);
            const res = await SizeHttpService.addOrUpdateSize(upsert);
            const updatedList = upsertArrayElement(sizes, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetSizes(updatedList));
            await setSizeMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedSize ? 'Size updated successfully.' : 'Size added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating size data.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeSize = async () => {
        if (!deletingSize) return;
        const updatedSize = { ...deletingSize, isActive: false };
        setIsLoading(true);
        try {
            const res = await SizeHttpService.deleteSize(updatedSize.id);
            const updatedList = upsertArrayElement(sizes, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetSizes(updatedList));
            await setSizeMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Size deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting size.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedSize(null);
    };

    const edit = (selectedRow : ISize) => {
        setSelectedSize(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : ISize) : void => {
        setDeletingSize(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingSize(null);
    };

    const addButtonTooltip = () : string => {
        let message = 'Add';

        if (!userHasRights.addRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Size', RightGrantsEnum.Add);
        }

        return message;
    };

    const deleteButtonTooltip = (row : ISize) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Size', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const formatPacks = (packIds : Array<number>) : string => {
        let string = packs
            .filter(x => packIds.includes(x.id))
            .map(x => x.code).toString().replace(/,/g, ', ');
        if (string.length > 85) {
            string = string.substring(0, 86) + '...';
        }
        return string;
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo<ISizeFormValues>(() => {
        return SizeModelHelper.createFormValues(selectedSize, packs);
    }, [selectedSize, isDialogOpen, packs]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    const rows = useMemo<Array<ISize>>(() => {
        if (props.selectedCommodity) {
            const filteredPacks = packs.filter(x => x.commodityId === props.selectedCommodity?.id);
            return sizes.filter(x => x.packIds.some(y => filteredPacks.some(z => z.id === y)));
        }
        return sizes;
    }, [props.selectedCommodity, packs, sizes]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <>
            <Card className={'fdc hfill'}>
                <CustomTable<ISize>
                        
                    enableAdding
                    // addTooltipRightValidation={!userHasRights.addRight}
                    disableAddButton={!userHasRights.addRight || isLoading}
                    addTooltip={addButtonTooltip()}
                    addFunction={openModal}
                    // addTooltipOnClick={() : void => requestAccess('Add')}
                
                    enableEditing
                    // editTooltipRightValidation={!userHasRights.editRight}
                    disableEditButton={!userHasRights.editRight || isLoading}
                    editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Size', RightGrantsEnum.Edit) : 'Edit'}
                    editFunction={edit}
                    // editTooltipOnClick={() : void => requestAccess('Edit')}

                    enableDeleting
                    // deleteTooltipRightValidation={!userHasRights.deleteRight}
                    disableDeleteButton={(row : ISize) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                    deleteTooltip={deleteButtonTooltip}
                    deleteFunction={onDelete}
                    // deleteTooltipOnClick={() : void => requestAccess('Delete')}
                
                    enableSorting
                    enableFiltering
                    enablePagination
                    columns={[
                        { title: 'Code', field: 'code', width: 100, enableFiltering: true, enableSorting: true },
                        { title: 'Name', field: 'name', width: 200, enableFiltering: true, enableSorting: true },
                        { title: 'Packs', field: 'packIds', width: 500, formatFunction: formatPacks, enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={rows}
                    initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                    pageSizes={[50, 150, 250, 500, 1000]}
                    pageHeight={340}
                    isActive={row => row.isActive}
                />
            </Card>
            {isDialogOpen &&
                <ProjectDialog
                    title={selectedSize ? 'Edit Size' : 'Add Size'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'lg'}
                    fullWidth
                >
                    <SizeForm
                        isLoading={isLoading}
                        selectedCommodity={props.selectedCommodity}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Size'}
                open={!!deletingSize}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeSize}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </>
    );
};

export default SizeScreen;
